import { useEffect, useState } from 'react'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { useDarf } from './Rechte'
import { beschriftung, ohneVortrag, t } from './sprache'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { ohneStatistikkonten } from './Festkonten'
import type { Auswahleintrag } from './Auswahlfeld'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { Blattabschnitt } from './Stammdaten'
import type { Sprachschluessel } from './sprache'
import type { Listeneintrag } from './typen'

/*
  Was eine Zeile der Kapitalflussrechnung aus Bilanz und GuV zieht (R857,
  R1323–R1328). Das Fenster darum ist seit R858 das allgemeine
  Eigenschaften-Fenster (`Eigenschaftsfenster.tsx`).
*/

/** Die Vorräte, aus denen eine Referenz gewählt wird. */
type Vorraete = {
  position: Listeneintrag[]
  konto: Listeneintrag[]
  kontengruppe: Listeneintrag[]
  bewegungsart: Listeneintrag[]
  kfr_zeile: Listeneintrag[]
  positionsgliederung: Listeneintrag[]
}

/** Die Quelle eines Verweises in der Auswahl: Position oder Konto (R1323, R1369). */
export function quellwert(werte: Record<string, string | undefined>): string {
  return werte['konto'] ? `konto:${werte['konto']}` : werte['position'] ? `position:${werte['position']}` : ''
}

/** Was eine gewählte Quelle in die beiden Felder schreibt — die andere wird leer. */
export function quellfelder(wert: string): { position: string | null; konto: string | null } {
  return wert.startsWith('konto:')
    ? { position: null, konto: wert.slice('konto:'.length) }
    : wert.startsWith('position:')
      ? { position: wert.slice('position:'.length), konto: null }
      : { position: null, konto: null }
}

/**
 * **Die Bewegungsarten eines Verweises** (2.8 Abschnitt 13.49, R1369) —
 * ausdrücklich angekreuzt, durch Komma getrennt gespeichert. *Alle* und *alle
 * übrigen* gibt es nicht mehr.
 */
export function artwerte(werte: Record<string, string | undefined>): string[] {
  return (werte['bewegungsarten'] ?? '').split(',').filter(Boolean)
}

/** Was eine Liste angekreuzter Bewegungsarten schreibt (R1369). */
export function artfelder(arten: string[]): Record<string, string | null> {
  return { bewegungsarten: arten.join(','), bewegungsart: null, alle_bewegungsarten: 'false' }
}

/**
 * **Warum ein Verweis so nicht geht — oder `null`** (R1355, R1369): Leer ist
 * keine Wahl — eine Quelle und mindestens eine Bewegungsart. Dieselbe Prüfung
 * wie auf der Rechenseite.
 */
export function referenzfehler(quelle: string, arten: string[]): Sprachschluessel | null {
  return !quelle || arten.length === 0 ? 'kfr.referenz.unvollstaendig.meldung' : null
}

/** Ein Verweis im Anlegefenster, noch nicht geschrieben (R1357). */
export type Referenzentwurf = { quelle: string; arten: string[]; umkehren: boolean }

/** Die erste Zeile im Anlegefenster: „Bitte auswählen", nichts angekreuzt. */
export const LEERE_REFERENZ: Referenzentwurf = { quelle: '', arten: [], umkehren: false }

/** Ob das Anlegefenster mit diesen Verweisen anlegen darf: mindestens einer, jeder vollständig. */
export function referenzenVollstaendig(zeilen: Referenzentwurf[]): boolean {
  return zeilen.length > 0 && zeilen.every((z) => referenzfehler(z.quelle, z.arten) === null)
}

/**
 * **Die Ankreuzliste der Bewegungsarten** (2.8 Abschnitt 13.49, R1369) — im
 * Feld steht, was angekreuzt ist, oder „Bitte auswählen"; aufgeklappt jede
 * Bewegungsart mit Kästchen und *alle ankreuzen*, das jede heutige einzeln
 * setzt. Eine später angelegte steht nicht darin.
 */
export function Bewegungsartenwahl({
  werte,
  eintraege,
  aufWahl,
  abgeschaltet,
  beschriftung,
}: {
  werte: string[]
  eintraege: Auswahleintrag[]
  aufWahl: (werte: string[]) => void
  abgeschaltet?: boolean
  beschriftung: string
}) {
  const [offen, setOffen] = useState(false)
  const text =
    werte.length === 0
      ? t('auswahl.bitte')
      : werte.map((w) => eintraege.find((e) => e.wert === w)?.text ?? w).join(', ')

  return (
    <div className="bewegungsartenwahl">
      <button
        type="button"
        className="auswahlfeld feldknopf"
        aria-expanded={offen}
        aria-label={beschriftung}
        title={text}
        disabled={abgeschaltet}
        onClick={() => setOffen(!offen)}
      >
        {text}
      </button>
      {offen && (
        <div className="ankreuzliste" role="group" aria-label={beschriftung}>
          <button type="button" className="schmal" onClick={() => aufWahl(eintraege.map((e) => e.wert))}>
            {t('kfreigenschaften.alleankreuzen')}
          </button>
          <ul>
            {eintraege.map((e) => (
              <li key={e.wert}>
                <label>
                  <input
                    type="checkbox"
                    checked={werte.includes(e.wert)}
                    onChange={(ereignis) =>
                      aufWahl(
                        ereignis.target.checked
                          ? eintraege.map((x) => x.wert).filter((w) => w === e.wert || werte.includes(w))
                          : werte.filter((w) => w !== e.wert),
                      )
                    }
                  />{' '}
                  {e.text}
                </label>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setOffen(false)}>
            {t('kfreigenschaften.arten.fertig')}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * **Die Folge der Positionen, wie die Bilanz sie zeigt** — Elter vor Kind,
 * Geschwister nach ihrer Sortierung.
 *
 * Die Liste der Positionen kommt nach Kennung: `POS-P-C-1` ist C.IV,
 * `POS-P-C-3` ist C.II. In dieser Folge stand am 2026-10-02 *C.II* vor
 * *C.I* im Fenster. Gelesen wird nur die heute geltende Gliederung.
 */
export function gliederungsfolge(gliederung: Listeneintrag[]): Map<string, number> {
  const kinder = new Map<string, Listeneintrag[]>()
  const unten = new Set<string>()
  for (const g of gliederung) {
    if (g.werte['gueltig_bis']) {
      continue
    }
    const eltern = g.werte['eltern'] ?? ''
    kinder.set(eltern, [...(kinder.get(eltern) ?? []), g])
    unten.add(g.werte['position'] ?? '')
  }

  const folge = new Map<string, number>()
  const besuche = (eltern: string) => {
    const reihe = [...(kinder.get(eltern) ?? [])].sort(
      (a, b) => Number(a.werte['sortierung'] ?? 0) - Number(b.werte['sortierung'] ?? 0),
    )
    for (const k of reihe) {
      const position = k.werte['position'] ?? ''
      if (!folge.has(position)) {
        folge.set(position, folge.size)
        besuche(position)
      }
    }
  }
  // Die Wurzeln sind die Eltern, die selbst unter niemandem stehen.
  for (const wurzel of [...kinder.keys()].filter((e) => !unten.has(e)).sort()) {
    besuche(wurzel)
  }
  return folge
}


/**
 * **Die Achsen einer Referenz zur Auswahl** — eine Stelle für das
 * Eigenschaften-Fenster und das Anlegen per Rechtsklick (2.8 Abschnitt 13.36,
 * R1124), damit beide dieselben Listen in derselben Folge zeigen.
 *
 * **Bilanz und GuV** (R1323, Entscheidung vom 2026-10-06): Bis dahin standen
 * hier nur Bilanzpositionen. Seit eine Zeile frei zieht, gehört die GuV dazu —
 * der Zinsaufwand, herausgerechnet und in der Finanzierung wieder gezeigt.
 * Jede Position steht unter ihrer Seite, Bilanz vor GuV, in der Folge der
 * Gliederung. Die Bewegungsarten nach der Nummer im Namen (R830) — die Liste
 * kommt in der Reihenfolge der Anlage, und „500" zwischen „040" und „610"
 * sucht niemand —, **ohne Vortrag** (R1061); eine Referenz, die schon einen
 * trägt, zeigt ihn weiter an.
 */
export function regelachsen(
  position: Listeneintrag[],
  bewegungsart: Listeneintrag[],
  positionsgliederung: Listeneintrag[],
): {
  stelle: (position: string | undefined) => number
  positionen: Auswahleintrag[]
  arten: (gewaehlt?: string) => Auswahleintrag[]
} {
  const rang = gliederungsfolge(positionsgliederung)
  const stelle = (p: string | undefined) => (p ? (rang.get(p) ?? rang.size) : -1)
  const seite = (p: Listeneintrag) =>
    p.werte['art'] === 'aktiva' || p.werte['art'] === 'passiva'
      ? 0
      : p.werte['art'] === 'aufwand' || p.werte['art'] === 'ertrag'
        ? 1
        : -1
  const positionen = position
    .filter((p) => seite(p) >= 0)
    .sort((a, b) => seite(a) - seite(b) || stelle(a.schluessel) - stelle(b.schluessel))
    .map((p) => ({
      ...ausStammdatum(p),
      gruppe: t(seite(p) === 0 ? 'kfreigenschaften.gruppe.bilanz' : 'kfreigenschaften.gruppe.guv'),
    }))
  const arten = (gewaehlt?: string) =>
    ohneVortrag(bewegungsart, gewaehlt)
      .map(ausStammdatum)
      .sort((a, b) => a.text.localeCompare(b.text, 'de', { numeric: true }))
  return { stelle, positionen, arten }
}

/**
 * **Die Quellen einer Referenz** (R1323): die Positionen aus
 * `regelachsen`, darunter die Konten nach ihrer Nummer. Der Wert sagt, was
 * gewählt ist — `position:…` oder `konto:…` —, damit beide Felder aus einer
 * Liste kommen.
 */
export function quellen(positionen: Auswahleintrag[], konto: Listeneintrag[]): Auswahleintrag[] {
  return [
    // Nur eindeutig benannte Positionen und Konten — *überall* gibt es nicht (R1369).
    ...positionen.map((p) => ({ ...p, wert: `position:${p.wert}` })),
    ...konto
      .map(ausStammdatum)
      .sort((a, b) => a.text.localeCompare(b.text, 'de', { numeric: true }))
      .map((k) => ({ ...k, wert: `konto:${k.wert}`, gruppe: t('kfreigenschaften.gruppe.konten') })),
  ]
}

/**
 * **Die beiden Listen einer Referenz** — eine Stelle für das
 * Eigenschaftsfenster und das Anlegefenster (2.8 Abschnitt 13.46, R1356,
 * R1357): Referenz auf Position/Konto mit *überall* vorn, Bewegungsart mit
 * *alle* und *alle übrigen* vorn.
 */
export function referenzlisten(
  position: Listeneintrag[],
  bewegungsart: Listeneintrag[],
  positionsgliederung: Listeneintrag[],
  konto: Listeneintrag[],
): {
  stelle: (position: string | undefined) => number
  quellliste: Auswahleintrag[]
  artliste: (gewaehlt?: string) => Auswahleintrag[]
} {
  const { stelle, positionen, arten } = regelachsen(position, bewegungsart, positionsgliederung)
  return {
    stelle,
    // Ein Statistikkonto ist keine Quelle: Es hängt selbst unter seiner
    // Position (2.1 Abschnitt 3.6, R1727).
    quellliste: quellen(positionen, ohneStatistikkonten(konto, (k) => k.schluessel)),
    // Jede Bewegungsart einzeln; *alle* und *alle übrigen* gibt es nicht (R1369).
    artliste: (gewaehlt?: string) => arten(gewaehlt),
  }
}

/**
 * Die Listen einer Referenz, geladen — `null`, solange sie unterwegs sind
 * oder `gebraucht` nein sagt. Seit dem 2026-10-06 mit den Konten (R1356):
 * Bis dahin bot das Anlegefenster nur Positionen an.
 */
export function useReferenzlisten(gebraucht = true): ReturnType<typeof referenzlisten> | null {
  const [listen, setListen] = useState<ReturnType<typeof referenzlisten> | null>(null)

  useEffect(() => {
    if (!gebraucht) {
      return
    }

    let gilt = true
    void Promise.all([
      api.stammdatenliste('position'),
      api.stammdatenliste('bewegungsart'),
      api.stammdatenliste('positionsgliederung'),
      api.stammdatenliste('konto'),
    ]).then(([p, b, g, k]) => {
      if (gilt) {
        setListen(referenzlisten(p.eintraege, b.eintraege, g.eintraege, k.eintraege))
      }
    })
    return () => {
      gilt = false
    }
  }, [gebraucht])

  return listen
}

/**
 * **Die Referenztabelle im Anlegefenster** (2.8 Abschnitt 13.46, R1357) —
 * dieselben Spalten wie unter *Referenzierte Elemente aus Bilanz/GuV* im
 * Eigenschaftsfenster, nur noch nicht geschrieben: Geschrieben wird mit
 * *Anlegen*, alles in einem Handgriff. Eine Zeile steht vorbelegt mit
 * „Bitte auswählen" da.
 */
export function Referenzentwurfstabelle({
  zeilen,
  aufAenderung,
  abgeschaltet,
  einzeln = false,
}: {
  zeilen: Referenzentwurf[]
  aufAenderung: (zeilen: Referenzentwurf[]) => void
  abgeschaltet?: boolean
  /** Genau eine Referenz, ohne Hinzufügen und Entfernen — *Neue Referenz …* (2.1 Abschnitt 3.6, R1705). */
  einzeln?: boolean
}) {
  const listen = useReferenzlisten()
  // Die Spalten ziehen sich wie im Eigenschaftsfenster (R833).
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('kfreigenschaften:regeln')

  if (!listen) {
    return null
  }

  const setze = (i: number, teil: Partial<Referenzentwurf>) =>
    aufAenderung(zeilen.map((z, j) => (j === i ? { ...z, ...teil } : z)))

  // Ein Eingabefeld und keine Markierspalte — wie im Eigenschaftsfenster (R987).
  const umkehrfeld = (an: boolean, aufWahl: (an: boolean) => void) => (
    <input
      type="checkbox"
      checked={an}
      disabled={abgeschaltet}
      aria-label={t('stammdaten.feld.vorzeichen_umkehren')}
      onChange={(e) => aufWahl(e.target.checked)}
    />
  )

  return (
    <div className="tabellenrahmen">
      <table ref={rahmen} className="kfrregeln ziehbaretabelle">
        <Spaltenmassleiste masse={masse} />
        <thead>
          <tr>
            <th title={t('kfreigenschaften.quelle.zweck')}>{t('kfreigenschaften.quelle')}</th>
            <th title={t('stammdaten.zweck.kfr_zuordnung.bewegungsarten')}>{t('stammdaten.feld.bewegungsarten')}</th>
            <th title={t('stammdaten.zweck.kfr_zuordnung.vorzeichen_umkehren')}>
              {t('stammdaten.feld.vorzeichen_umkehren')}
            </th>
            {!einzeln && <th aria-label={t('kfreigenschaften.entfernen')} />}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, i) => (
            <tr key={i}>
              <td>
                <Auswahlfeld
                  wert={z.quelle}
                  eintraege={listen.quellliste}
                  leer={t('auswahl.bitte')}
                  abgeschaltet={abgeschaltet}
                  beschriftung={t('kfreigenschaften.quelle')}
                  aufWahl={(w) => setze(i, { quelle: w })}
                />
              </td>
              <td>
                <Bewegungsartenwahl
                  werte={z.arten}
                  eintraege={listen.artliste()}
                  abgeschaltet={abgeschaltet}
                  beschriftung={t('stammdaten.feld.bewegungsarten')}
                  aufWahl={(arten) => setze(i, { arten })}
                />
              </td>
              <td>{umkehrfeld(z.umkehren, (an) => setze(i, { umkehren: an }))}</td>
              {!einzeln && (
              <td>
                {zeilen.length > 1 && (
                  <button
                    type="button"
                    className="schmal"
                    disabled={abgeschaltet}
                    onClick={() => aufAenderung(zeilen.filter((_, j) => j !== i))}
                  >
                    {t('kfreigenschaften.entfernen')}
                  </button>
                )}
              </td>
              )}
            </tr>
          ))}
          {!einzeln && (
          <tr className="neuzeile">
            <td colSpan={3} />
            <td>
              <button type="button" disabled={abgeschaltet} onClick={() => aufAenderung([...zeilen, LEERE_REFERENZ])}>
                {t('kfreigenschaften.neu')}
              </button>
            </td>
          </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

/** Die Referenzen, die in diese Zeile ziehen (R1323). Die Zeile bei Auszahlung ist fort (R1327). */
export function regelnDerZeile(regeln: Listeneintrag[], zeile: string): Listeneintrag[] {
  return regeln.filter((r) => r.werte['zeile'] === zeile)
}

/**
 * **Referenzierte Elemente aus Bilanz/GuV** — die Referenzen der Zeile, zum
 * Ändern, Anlegen und Entfernen (R857, R1328).
 *
 * Drei Angaben je Referenz, wie der Auftraggeber sie am 2026-10-06 benannt
 * hat: Quelle (Position oder Konto), Bewegungsarten, Vorzeichen umkehren. Die
 * Zeile steht nicht dabei — es ist die, deren Fenster offen ist.
 *
 * Jede Zelle schreibt beim Wählen: Eine Referenz hat keinen Namen, und ein
 * eigener Speichern-Knopf je Zeile wäre ein Handgriff, der nichts entscheidet.
 */
export function Kfrregeln({ zeile, aufAenderung }: { zeile: string; aufAenderung?: () => void }) {
  const [regeln, setRegeln] = useState<Listeneintrag[] | null>(null)
  const [vorrat, setVorrat] = useState<Vorraete | null>(null)
  const [neu, setNeu] = useState<Record<string, string>>({})
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  // Die Spalten ziehen sich wie an jeder Tabelle (R833).
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('kfreigenschaften:regeln')
  const darf = useDarf()

  async function lies() {
    setRegeln((await api.stammdatenliste('kfr_zuordnung')).eintraege)
  }

  useEffect(() => {
    let gilt = true
    void Promise.all([
      api.stammdatenliste('kfr_zuordnung'),
      api.stammdatenliste('position'),
      api.stammdatenliste('kontengruppe'),
      api.stammdatenliste('bewegungsart'),
      api.stammdatenliste('kfr_zeile'),
      api.stammdatenliste('positionsgliederung'),
      api.stammdatenliste('konto'),
    ]).then(([z, p, k, b, kz, pg, ko]) => {
      if (!gilt) {
        return
      }
      setRegeln(z.eintraege)
      setVorrat({
        position: p.eintraege,
        konto: ko.eintraege,
        kontengruppe: k.eintraege,
        bewegungsart: b.eintraege,
        kfr_zeile: kz.eintraege,
        positionsgliederung: pg.eintraege,
      })
    })
    return () => {
      gilt = false
    }
  }, [zeile])

  if (!regeln || !vorrat) {
    return null
  }

  // **Die Fondszeilen ziehen nichts, sie bestimmen den Fonds** (2.8 Abschnitt
  // 13.42, R1338): Am Anfang und am Ende der Periode steht dieselbe Liste,
  // für beide Zeilensätze (R1340).
  const rolle = vorrat.kfr_zeile.find((z) => z.schluessel === zeile)?.werte['rolle']
  // **Eine Summe zieht nichts selbst** (2.8 Abschnitt 13.43, R1351): Ihr
  // Fenster nennt, was sie zählt, statt Referenzen anzubieten — und ihr Format
  // (13.46, R1359), auch an Zeile 40.
  const summe = vorrat.kfr_zeile.find((z) => z.schluessel === zeile)
  const summenteil = summe?.werte['art'] === 'summe' && (
    <>
      <Kfrzaehlweise zeile={summe} />
      <Summenformat zeile={summe} aufAenderung={aufAenderung} />
    </>
  )

  if (rolle === 'fonds_ende' || rolle === 'fonds_anfang') {
    return (
      <>
        <Fondsreferenzen vorrat={vorrat} aufAenderung={aufAenderung} />
        {summenteil}
      </>
    )
  }

  // **Eine Position mit Positionen darunter zieht weiter selbst** (2.1
  // Abschnitt 3.6, R1709): Ihr Fenster nennt, was sie zählt, und zeigt ihre
  // Referenzen. Nur eine Summenzeile zieht nichts.
  const untersumme = summe?.werte['zaehlweise'] === 'unterzeilen'
  if (summenteil && !untersumme) {
    return summenteil
  }

  // Hier stand bis zum 2026-10-06 die Auswahl der Zeile und der Zeile bei
  // Auszahlung, beide aus den Zeilen desselben Standards. Die Zeile ist die,
  // deren Fenster offen ist (R1328); die zweite gibt es nicht mehr (R1327).
  // Hier stand bis zum 2026-10-05 der Sonderfall der Zeile 13a, die CoKo
  // selbst füllte. Die Zeile gibt es nicht mehr (2.8 Abschnitt 13.32, R1096).
  // Hier stand bis zum 2026-10-14, welche Richtung CoKo aus dem Namen liest
  // (R1090). Die Vorzeichenmarke ist fort (2.1 Abschnitt 3.6, R1713).
  // Dieselben Listen wie im Anlegefenster (2.8 Abschnitt 13.46, R1356):
  // „überall", „alle" und „alle übrigen" stehen ausdrücklich darin (R1355).
  const { stelle, quellliste, artliste } = referenzlisten(
    vorrat.position, vorrat.bewegungsart, vorrat.positionsgliederung, vorrat.konto)

  const name = (liste: Listeneintrag[], schluessel: string) =>
    beschriftung(liste.find((e) => e.schluessel === schluessel)?.namen) || schluessel
  // Ein Statistikkonto steht unter der Position, ist aber keine Referenz
  // (2.1 Abschnitt 3.6, R1727, R1730) — es hat seine eigene Zeile im Bericht.
  const statistikkonten = new Set(
    vorrat.konto.filter((k) => k.werte['kontoart'] === 'statistik').map((k) => k.schluessel),
  )
  const eigene = regelnDerZeile(regeln, zeile).filter((r) => !statistikkonten.has(r.werte['konto'] ?? '')).sort(
    (a, b) =>
      Number(!!a.werte['konto']) - Number(!!b.werte['konto']) ||
      stelle(a.werte['position']) - stelle(b.werte['position']) ||
      (a.werte['konto'] ?? '').localeCompare(b.werte['konto'] ?? '', 'de', { numeric: true }) ||
      name(vorrat.bewegungsart, a.werte['bewegungsart'] ?? '').localeCompare(
        name(vorrat.bewegungsart, b.werte['bewegungsart'] ?? ''),
      ),
  )

  async function schreibe(tun: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await tun()
      aufAenderung?.()
      await lies()
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * **Eine Referenz ist vollständig und zieht nicht die ganze Bilanz** (2.8
   * Abschnitt 13.46, R1355) — dieselbe Prüfung wie im Anlegefenster und auf
   * der Rechenseite; die Absage soll hier stehen und nicht als Störung. Eine
   * Regel an der Kontengruppe (R66) hat ihre Quelle dort.
   */
  function fehler(werte: Record<string, string | null | undefined>): Sprachschluessel | null {
    if (werte['kontengruppe']) {
      return null
    }
    const w = werte as Record<string, string | undefined>
    return referenzfehler(quellwert(w), artwerte(w))
  }

  function pflege(regel: Listeneintrag, felder: Record<string, string | null>) {
    const absage = fehler({ ...regel.werte, ...felder })
    if (absage) {
      setMeldung(t(absage))
      return
    }
    void schreibe(() => api.stammdatumPflegen('kfr_zuordnung', regel.schluessel, { felder }))
  }

  function lege() {
    // **Leer heißt „Bitte auswählen"** (R1355): Erst wenn beide gewählt sind,
    // wird geschrieben.
    const absage = referenzfehler(neu['quelle'] ?? '', (neu['arten'] ?? '').split(',').filter(Boolean))
    if (absage) {
      setMeldung(t(absage))
      return
    }
    const felder: Record<string, string | null> = {
      zeile,
      ...quellfelder(neu['quelle'] ?? ''),
      ...artfelder((neu['arten'] ?? '').split(',').filter(Boolean)),
      vorzeichen_umkehren: neu['umkehren'] === 'true' ? 'true' : 'false',
    }
    void schreibe(async () => {
      await api.stammdatumAnlegen('kfr_zuordnung', '', {
        felder: Object.fromEntries(Object.entries(felder).filter(([, w]) => w !== null)) as Record<string, string>,
      })
      setNeu({})
    })
  }

  const kopf = (feld: string) => (
    <th title={t(`stammdaten.zweck.kfr_zuordnung.${feld}` as Sprachschluessel)}>
      {t(`stammdaten.feld.${feld}` as Sprachschluessel)}
    </th>
  )

  // **Ein Kästchen, das beim Klicken schreibt** — wie die Auswahlfelder daneben.
  const umkehrfeld = (an: boolean, aufWahl: (an: boolean) => void) => (
    <input
      type="checkbox"
      checked={an}
      disabled={laeuft || !darf('stammdatumPflegen')}
      aria-label={t('stammdaten.feld.vorzeichen_umkehren')}
      onChange={(e) => aufWahl(e.target.checked)}
    />
  )

  const feld = (
    wert: string,
    eintraege: Auswahleintrag[],
    leer: string | undefined,
    aufWahl: (w: string) => void,
    beschriftungstext: string,
  ) => (
    <Auswahlfeld
      wert={wert}
      eintraege={eintraege}
      leer={leer}
      // Ohne Recht zum Pflegen liest man die Regel nur (R1180).
      abgeschaltet={laeuft || !darf('stammdatumPflegen')}
      beschriftung={beschriftungstext}
      aufWahl={aufWahl}
    />
  )

  return (
    <>
    {summenteil}
    <Blattabschnitt
      kennung="kfreigenschaften.regeln"
      titel={t('kfreigenschaften.regeln')}
    >
      {meldung && <p className="hinweis">{meldung}</p>}
      <div className="tabellenrahmen">
      <table ref={rahmen} className="kfrregeln ziehbaretabelle">
        <Spaltenmassleiste masse={masse} />
        <thead>
          <tr>
            <th title={t('kfreigenschaften.quelle.zweck')}>{t('kfreigenschaften.quelle')}</th>
            {kopf('bewegungsarten')}
            {kopf('vorzeichen_umkehren')}
            {(darf('aussondern') || darf('stammdatumAnlegen')) && <th aria-label={t('kfreigenschaften.entfernen')} />}
          </tr>
        </thead>
        <tbody>
          {eigene.map((r) => (
            <tr key={r.schluessel}>
              <td>
                {/* Eine Regel an der Kontengruppe gilt nur für Statistikkonten
                    (R66) — sie wird hier gezeigt, aber nicht auf eine Position
                    umgehängt: Das wäre eine andere Regel. */}
                {r.werte['kontengruppe']
                  ? t('kfreigenschaften.kontengruppe', { name: name(vorrat.kontengruppe, r.werte['kontengruppe'] ?? '') })
                  : feld(quellwert(r.werte), quellliste, t('auswahl.bitte'),
                      (w) => pflege(r, quellfelder(w)), t('kfreigenschaften.quelle'))}
              </td>
              <td>
                <Bewegungsartenwahl
                  werte={artwerte(r.werte)}
                  eintraege={artliste()}
                  abgeschaltet={laeuft || !darf('stammdatumPflegen')}
                  beschriftung={t('stammdaten.feld.bewegungsarten')}
                  aufWahl={(arten) => pflege(r, artfelder(arten))}
                />
              </td>
              <td>
                {umkehrfeld(r.werte['vorzeichen_umkehren'] === 'true', (an) =>
                  pflege(r, { vorzeichen_umkehren: an ? 'true' : 'false' }),
                )}
              </td>
              {(darf('aussondern') || darf('stammdatumAnlegen')) && (
              <td>
                {darf('aussondern') && (
                <button
                  type="button"
                  className="schmal"
                  disabled={laeuft}
                  onClick={() =>
                    void schreibe(() =>
                      api.aussondern('kfr_zuordnung', r.schluessel, { weg: 'entfernen', abPeriode: null, ziel: null }),
                    )
                  }
                >
                  {t('kfreigenschaften.entfernen')}
                </button>
                )}
              </td>
              )}
            </tr>
          ))}
          {eigene.length === 0 && (
            <tr>
              <td colSpan={4} className="hinweis">
                {t('kfreigenschaften.leer')}
              </td>
            </tr>
          )}
          {/* **Die neue Regel steht als letzte Zeile da** — dieselben Felder
              wie darüber, die Zeile ist schon diese. Ohne Recht zum Anlegen
              ist sie nicht da (R1180). */}
          {darf('stammdatumAnlegen') && (
          <tr className="neuzeile">
            <td>
              {feld(neu['quelle'] ?? '', quellliste, t('auswahl.bitte'),
                (w) => setNeu({ ...neu, quelle: w }), t('kfreigenschaften.quelle'))}
            </td>
            <td>
              <Bewegungsartenwahl
                werte={(neu['arten'] ?? '').split(',').filter(Boolean)}
                eintraege={artliste()}
                abgeschaltet={laeuft}
                beschriftung={t('stammdaten.feld.bewegungsarten')}
                aufWahl={(arten) => setNeu({ ...neu, arten: arten.join(',') })}
              />
            </td>
            <td>
              {umkehrfeld(neu['umkehren'] === 'true', (an) => setNeu({ ...neu, umkehren: an ? 'true' : '' }))}
            </td>
            <td>
              <button type="button" disabled={laeuft} onClick={lege}>
                {t('kfreigenschaften.neu')}
              </button>
            </td>
          </tr>
          )}
        </tbody>
      </table>
      </div>
    </Blattabschnitt>
    </>
  )
}

/** Der Satz zu jeder Zählweise einer Summe (R1347). */
const ZAEHLSATZ: Record<string, Sprachschluessel> = {
  abschnitt: 'kfreigenschaften.zaehlt.abschnitt',
  darueber: 'kfreigenschaften.zaehlt.darueber',
  seit_voriger: 'kfreigenschaften.zaehlt.seit_voriger',
  unterzeilen: 'kfreigenschaften.zaehlt.unterzeilen',
}

/**
 * **Was eine Summe zählt** (2.8 Abschnitt 13.43, R1351) — im Fenster jeder
 * Summe statt der Referenzen. Eine Summe zieht nichts selbst; bis zum
 * 2026-10-06 bot ihr Fenster *Referenz hinzufügen* an, und eine Referenz dort
 * wäre ins Leere gelaufen. Beim Cashflow eines Abschnitts steht der Abschnitt
 * dabei, mit Nummer und Namen, wie er am Band steht (R1333).
 */
export function Kfrzaehlweise({ zeile }: { zeile: Listeneintrag }) {
  const weise = zeile.werte['zaehlweise'] ?? ''
  const [abschnitt, setAbschnitt] = useState<string>('')

  useEffect(() => {
    if (weise !== 'abschnitt') {
      return
    }
    let gilt = true
    void api.stammdatenliste('kfr_abschnitt').then((liste) => {
      const band = liste.eintraege.find(
        (a) => a.werte['standard'] === zeile.werte['standard'] && a.werte['bereich'] === zeile.werte['bereich'],
      )
      if (gilt && band) {
        // Wie am Band: „1. Laufende Geschäftstätigkeit".
        setAbschnitt(`${band.code.endsWith('.') ? band.code : `${band.code}.`} ${beschriftung(band.namen)}`)
      }
    })
    return () => {
      gilt = false
    }
  }, [weise, zeile])

  const satz = ZAEHLSATZ[weise]
  if (!satz) {
    return null
  }

  return (
    <Blattabschnitt kennung="kfreigenschaften.zaehlt" titel={t('kfreigenschaften.zaehlt')}>
      <p className="erlaeuterung">{t(satz, { abschnitt })}</p>
    </Blattabschnitt>
  )
}

/** Die feste Auswahl für das Format einer Summenzeile (R1359) — dieselbe wie auf der Rechenseite. */
export const FORMATAUSWAHL = {
  schrift: ['fett', 'normal'],
  hintergrund: ['keiner', 'grau', 'blau', 'gruen', 'gelb', 'rot', 'violett'],
  schriftfarbe: ['grau', 'blau', 'gruen', 'rot', 'violett'],
} as const

/**
 * **Das Format einer Summenzeile** (2.8 Abschnitt 13.46, R1359) — Schrift,
 * Hintergrund, Schriftfarbe aus einer festen Auswahl. *Standard* ist die Form
 * ihrer Art; das Format gehört der Zeile und gilt für alle. Jede Wahl schreibt
 * sofort, wie die Referenzen daneben.
 */
export function Summenformat({ zeile, aufAenderung }: { zeile: Listeneintrag; aufAenderung?: () => void }) {
  const [werte, setWerte] = useState<Record<string, string>>({
    schrift: zeile.werte['schrift'] ?? '',
    hintergrund: zeile.werte['hintergrund'] ?? '',
    schriftfarbe: zeile.werte['schriftfarbe'] ?? '',
  })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const darf = useDarf()

  async function waehle(feld: keyof typeof FORMATAUSWAHL, wert: string) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await api.stammdatumPflegen('kfr_zeile', zeile.schluessel, { felder: { [feld]: wert || null } })
      setWerte((w) => ({ ...w, [feld]: wert }))
      aufAenderung?.()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  const auswahl = (feld: keyof typeof FORMATAUSWAHL) => (
    <label className="feld">
      <span className="name">{t(`stammdaten.feld.${feld}` as Sprachschluessel)}</span>
      <Auswahlfeld
        wert={werte[feld] ?? ''}
        eintraege={FORMATAUSWAHL[feld].map((w) => ({
          wert: w,
          text: t(`stammdaten.wert.${feld}.${w}` as Sprachschluessel),
        }))}
        // „Standard" ist die Form ihrer Art (R1359) und ein wählbarer Eintrag.
        leer={t('kfreigenschaften.format.standard')}
        abgeschaltet={laeuft || !darf('stammdatumPflegen')}
        beschriftung={t(`stammdaten.feld.${feld}` as Sprachschluessel)}
        aufWahl={(w) => void waehle(feld, w)}
      />
    </label>
  )

  return (
    <Blattabschnitt kennung="kfreigenschaften.format" titel={t('kfreigenschaften.format')}>
      {meldung && <p className="hinweis">{meldung}</p>}
      <div className="feldzeile">
        {auswahl('schrift')}
        {auswahl('hintergrund')}
        {auswahl('schriftfarbe')}
      </div>
    </Blattabschnitt>
  )
}

/**
 * **Was zum Finanzmittelfonds gehört** (2.8 Abschnitt 13.42, R1338–R1340) —
 * im Fenster der Zeilen *Finanzmittelfonds am Ende* und *am Anfang*.
 *
 * **Nur die Quelle**: eine Bilanzposition mit ihren Unterpositionen oder ein
 * Konto. Ein Konto gehört ganz zum Fonds oder gar nicht — Bewegungsart und
 * Vorzeichen gibt es hier nicht. Eine Liste für alle Zeilensätze; bis zum
 * 2026-10-06 stand der Fonds als Eigenschaft an Kontengruppe und Konto (R1339).
 */
function Fondsreferenzen({ vorrat, aufAenderung }: { vorrat: Vorraete; aufAenderung?: () => void }) {
  const [referenzen, setReferenzen] = useState<Listeneintrag[] | null>(null)
  const [neu, setNeu] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const darf = useDarf()
  // Die Spalten ziehen sich wie an jeder Tabelle (R833).
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('kfreigenschaften:fonds')

  async function lies() {
    setReferenzen((await api.stammdatenliste('kfr_fonds')).eintraege)
  }

  useEffect(() => {
    void lies()
  }, [])

  if (!referenzen) {
    return null
  }

  // Bilanzpositionen und Konten — die GuV hat keinen Bestand, der Fonds sein könnte.
  const { positionen } = regelachsen(vorrat.position, vorrat.bewegungsart, vorrat.positionsgliederung)
  const bilanz = vorrat.position.filter((p) => p.werte['art'] === 'aktiva' || p.werte['art'] === 'passiva')
  const quellliste = quellen(
    positionen.filter((p) => bilanz.some((b) => b.schluessel === p.wert)),
    vorrat.konto,
  )

  async function schreibe(tun: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await tun()
      aufAenderung?.()
      await lies()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  const feld = (wert: string, aufWahl: (w: string) => void) => (
    <Auswahlfeld
      wert={wert}
      eintraege={quellliste}
      abgeschaltet={laeuft || !darf('stammdatumPflegen')}
      beschriftung={t('kfreigenschaften.quelle')}
      aufWahl={aufWahl}
    />
  )

  return (
    <Blattabschnitt kennung="kfreigenschaften.fonds" titel={t('kfreigenschaften.fonds')}>
      {meldung && <p className="hinweis">{meldung}</p>}
      <p className="erlaeuterung">{t('kfreigenschaften.fonds.hinweis')}</p>
      <div className="tabellenrahmen">
        <table ref={rahmen} className="kfrregeln ziehbaretabelle">
          <Spaltenmassleiste masse={masse} />
          <thead>
            <tr>
              <th title={t('kfreigenschaften.fonds.zweck')}>{t('kfreigenschaften.quelle')}</th>
              {(darf('aussondern') || darf('stammdatumAnlegen')) && <th aria-label={t('kfreigenschaften.entfernen')} />}
            </tr>
          </thead>
          <tbody>
            {referenzen.map((r) => (
              <tr key={r.schluessel}>
                <td>{feld(quellwert(r.werte), (w) => void schreibe(() => api.stammdatumPflegen('kfr_fonds', r.schluessel, { felder: quellfelder(w) })))}</td>
                {(darf('aussondern') || darf('stammdatumAnlegen')) && (
                  <td>
                    {darf('aussondern') && (
                      <button
                        type="button"
                        className="schmal"
                        disabled={laeuft}
                        onClick={() =>
                          void schreibe(() =>
                            api.aussondern('kfr_fonds', r.schluessel, { weg: 'entfernen', abPeriode: null, ziel: null }),
                          )
                        }
                      >
                        {t('kfreigenschaften.entfernen')}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {referenzen.length === 0 && (
              <tr>
                <td colSpan={2} className="hinweis">
                  {t('kfreigenschaften.fonds.leer')}
                </td>
              </tr>
            )}
            {darf('stammdatumAnlegen') && (
              <tr className="neuzeile">
                <td>{feld(neu, setNeu)}</td>
                <td>
                  <button
                    type="button"
                    disabled={laeuft || !neu}
                    onClick={() =>
                      void schreibe(async () => {
                        const felder = Object.fromEntries(
                          Object.entries(quellfelder(neu)).filter(([, w]) => w !== null),
                        ) as Record<string, string>
                        await api.stammdatumAnlegen('kfr_fonds', '', { felder })
                        setNeu('')
                      })
                    }
                  >
                    {t('kfreigenschaften.neu')}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Blattabschnitt>
  )
}
